import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useRegisterAccount } from '@services/useRegisterAccount';
import { useNotifications } from '@hooks/useNotifications';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useGoogleAuthService } from '@services/useGoogleAuthService';
import { useData } from '@hooks/useData';
import { useGetCart } from '@services/useGetCart';
import { Modules, useModule } from '@hooks/useModule';

export const CreateAccountCard: FC = () => {
    const { t, i18n } = useTranslation();
    const { isModuleActive } = useModule();
    const { login } = useData();
    const { navigate } = useCustomNavigate();
    const { request: requestCart } = useGetCart();
    const { handleGoogleLogin, lastResponseTime } = useGoogleAuthService();

    const translations = {
        title: t('create-account-card-component.title'),
        subtitle: t('create-account-card-component.subtitle'),
        createAccountWithGoogleButton: t('create-account-card-component.create-account-with-google.button'),
        fillForm: t('create-account-card-component.fill-form'),
    };

    const handleLoginSuccess = (response: CredentialResponse) => {
        handleGoogleLogin(response.credential ?? '');
    };

    useEffect(() => {
        if (login) {
            navigate('/');
            // request cart information
            requestCart({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, i18n.language]);

    return (
        <AuthenticationCard title={translations.title} subtitle={translations.subtitle}>
            <>
                {isModuleActive(Modules.googleAuth) ? (
                    <>
                        {/* create account with google */}
                        <GoogleLogin onSuccess={handleLoginSuccess} />
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    </>
                ) : (
                    <></>
                )}

                <hr className="my-0" />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                <Typography align="center" fontSize="14">
                    {translations.fillForm}
                </Typography>
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <CreateAccountForm />
            </>
        </AuthenticationCard>
    );
};

interface CreateAccountFormErrors {
    name: string[];
    lastName: string[];
    phoneNumber: string[];
    over18: string[];
    email: string[];
    password: 'valid' | 'invalid' | null;
    confirmPassword: string[];
    termsAndConditions: string[];
}

const CreateAccountForm: FC = () => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { t } = useTranslation();
    const { request: requestRegisterAccount, loading, error: errorRegisterAccount, lastResponseTime } = useRegisterAccount();

    const translations = {
        nameLabel: t('create-account-card-component.name.label'),
        lastNameLabel: t('create-account-card-component.last-name.label'),
        phoneNumberLabel: t('create-account-card-component.phone-number.label'),
        over18Label: t('create-account-card-component.over-18.label'),
        emailLabel: t('create-account-card-component.email.label'),
        passwordLabel: t('create-account-card-component.password.label'),
        confirmPasswordLabel: t('create-account-card-component.confirm-password.label'),
        termsAndConditionsLabel: t('create-account-card-component.terms-and-conditions.label'),
        termsAndConditionsLink: t('create-account-card-component.terms-and-conditions.link'),
        createAccountButton: t('create-account-card-component.create-account.button'),
        nameRequiredError: t('create-account-card-component.name.required-error'),
        lastNameRequiredError: t('create-account-card-component.last-name.required-error'),
        phoneNumberRequiredError: t('create-account-card-component.phone-number.required-error'),
        emailRequiredError: t('create-account-card-component.email.required-error'),
        emailInvalidError: t('create-account-card-component.email.invalid-error'),
        confirmPasswordRequiredError: t('create-account-card-component.confirm-password.required-error'),
        confirmPasswordMatchError: t('create-account-card-component.confirm-password.match-error'),
        didNotAcceptTermsAndConditionsError: t('create-account-card-component.terms-and-conditions.did-not-accept-error'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [over18, setOver18] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
    const [validPassword, setValidPassword] = useState<boolean>(false);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<CreateAccountFormErrors>({
        name: [],
        lastName: [],
        phoneNumber: [],
        over18: [],
        email: [],
        password: null,
        confirmPassword: [],
        termsAndConditions: [],
    });

    const handleOnClickCreateAccount = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.lastName.length === 0 &&
            errors.phoneNumber.length === 0 &&
            errors.over18.length === 0 &&
            errors.email.length === 0 &&
            errors.password === 'valid' &&
            errors.confirmPassword.length === 0 &&
            errors.termsAndConditions.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => CreateAccountFormErrors = () => {
        const newErrors: CreateAccountFormErrors = {
            name: [],
            lastName: [],
            phoneNumber: [],
            over18: [],
            email: [],
            password: validPassword ? 'valid' : 'invalid',
            confirmPassword: [],
            termsAndConditions: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !lastName.trim() && newErrors.lastName.push(translations.lastNameRequiredError);
        !phoneNumber.trim() && newErrors.phoneNumber.push(translations.phoneNumberRequiredError);
        // check if email is inserted
        if (!email.trim()) {
            newErrors.email.push(translations.emailRequiredError);
        } else {
            // check if email is valid
            !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        }
        // confirm password
        !confirmPassword.trim() && newErrors.confirmPassword.push(translations.confirmPasswordRequiredError);
        newErrors.confirmPassword.length === 0 &&
            password !== confirmPassword &&
            newErrors.confirmPassword.push(translations.confirmPasswordMatchError);
        // terms and conditions
        !termsAndConditions && newErrors.termsAndConditions.push(translations.didNotAcceptTermsAndConditionsError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        requestRegisterAccount({
            first_name: name,
            last_name: lastName,
            phone: phoneNumber,
            over_18: over18,
            email: email,
            password: password,
        });
    };

    // if created account successfully, go to login page
    // if not, show error
    useEffect(() => {
        if (lastResponseTime) {
            if (errorRegisterAccount) {
                showDefaultNotification({ type: 'error', message: errorRegisterAccount });
            } else {
                navigate(`/${SITE_URLS.login}?verificationEmailSent`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form
            formRef={formRef}
            classes="d-flex flex-column"
            onEnterPress={handleOnClickCreateAccount}
            onSubmit={() => submitForm()}>
            <>
                <Input
                    type={InputTypes.text}
                    variant={InputVariants.labeledInput}
                    label={`${translations.nameLabel}*`}
                    value={name}
                    onChange={(value) => setName(value)}
                    state={errors.name.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.name}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.text}
                    variant={InputVariants.labeledInput}
                    label={`${translations.lastNameLabel}*`}
                    value={lastName}
                    onChange={(value) => setLastName(value)}
                    state={errors.lastName.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.lastName}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.tel}
                    variant={InputVariants.labeledInput}
                    label={`${translations.phoneNumberLabel}*`}
                    value={phoneNumber}
                    onChange={(value) => setPhoneNumber(value)}
                    state={errors.phoneNumber.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.phoneNumber}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Checkbox
                    type={CheckboxType.normal}
                    name="over-18"
                    value="true"
                    checked={over18}
                    onChange={() => setOver18(!over18)}
                    label={
                        <Typography color="muted" fontSize="12">
                            {translations.over18Label}
                        </Typography>
                    }
                />
                {errors.over18.length > 0 && <ErrorMessages messages={errors.over18} />}
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.email}
                    variant={InputVariants.labeledInput}
                    label={`${translations.emailLabel}*`}
                    value={email}
                    onChange={(value) => setEmail(value)}
                    state={errors.email.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.email}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.passwordLabel}*`}
                    value={password}
                    onChange={(value) => setPassword(value)}
                    state={errors.password === 'invalid' ? InputStates.error : undefined}
                    checkRules={InputCheckRules.passwordBuild}
                    onValidationChange={setValidPassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.confirmPasswordLabel}*`}
                    value={confirmPassword}
                    onChange={(value) => setConfirmPassword(value)}
                    state={errors.confirmPassword.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.confirmPassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Checkbox
                    type={CheckboxType.normal}
                    name="terms-and-conditions"
                    value="accept"
                    checked={termsAndConditions}
                    onChange={() => setTermsAndConditions(!termsAndConditions)}
                    label={
                        <Typography color="muted" fontSize="12">
                            <>
                                {`${translations.termsAndConditionsLabel} `}
                                {/* #TODO: add link to Terms and Conditions page */}
                                <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                    {translations.termsAndConditionsLink}
                                </Typography>
                            </>
                        </Typography>
                    }
                />
                {errors.termsAndConditions.length > 0 && <ErrorMessages messages={errors.termsAndConditions} />}

                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Button
                    label={translations.createAccountButton}
                    mode={ButtonMode.primary}
                    onClick={() => handleOnClickCreateAccount()}
                    disabled={loading}
                />
            </>
        </Form>
    );
};
